import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../redux/slices/authSlice';
import './Header.css';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user, userType } = useSelector((state) => state.auth);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
    navigate('/');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navigationItems = [
    { label: 'Accueil', path: '/' },
    { label: 'Missions', path: '/missions' },
    { label: 'Professionnels', path: '/professionals' },
    { label: 'Organisations', path: '/pour-les-organisations' },
    { label: 'Actualités', path: '/blog' },
  ];

  const renderDesktopMenu = () => (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
      {navigationItems.map((item) => (
        <Button
          key={item.path}
          component={Link}
          to={item.path}
          sx={{
            color: 'text.primary',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'primary.main',
            },
          }}
        >
          {item.label}
        </Button>
      ))}
      
      {isAuthenticated ? (
        <>
          <Button
            onClick={handleMenuOpen}
            startIcon={userType === 'organization' ? <BusinessIcon /> : <PersonIcon />}
            sx={{
              color: 'text.primary',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {user?.name || 'Mon compte'}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => { 
              navigate(userType === 'organization' ? '/dashboard/org' : '/dashboard'); 
              handleMenuClose(); 
            }}>
              Tableau de bord
            </MenuItem>
            <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
              Mon profil
            </MenuItem>
            <MenuItem onClick={() => { navigate('/messages'); handleMenuClose(); }}>
              Messages
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              Se déconnecter
            </MenuItem>
          </Menu>
        </>
      ) : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            component={Link}
            to="/login"
            variant="outlined"
            sx={{
              borderRadius: '25px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Connexion
          </Button>
          <Button
            component={Link}
            to="/register"
            variant="contained"
            color="secondary"
            sx={{
              borderRadius: '25px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Inscription
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleMobileMenuToggle}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          padding: 2,
        },
      }}
    >
      <List>
        {navigationItems.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleMobileMenuToggle}
            sx={{
              color: 'text.primary',
              textDecoration: 'none',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        {isAuthenticated ? (
          <>
            <ListItem
              component={Link}
              to={userType === 'organization' ? '/dashboard/org' : '/dashboard'}
              onClick={handleMobileMenuToggle}
              sx={{ color: 'text.primary', textDecoration: 'none' }}
            >
              <ListItemText primary="Tableau de bord" />
            </ListItem>
            <ListItem
              component={Link}
              to="/profile"
              onClick={handleMobileMenuToggle}
              sx={{ color: 'text.primary', textDecoration: 'none' }}
            >
              <ListItemText primary="Mon profil" />
            </ListItem>
            <ListItem
              component={Link}
              to="/messages"
              onClick={handleMobileMenuToggle}
              sx={{ color: 'text.primary', textDecoration: 'none' }}
            >
              <ListItemText primary="Messages" />
            </ListItem>
            <ListItem
              onClick={() => {
                handleLogout();
                handleMobileMenuToggle();
              }}
              sx={{ color: 'text.primary', cursor: 'pointer' }}
            >
              <ListItemText primary="Se déconnecter" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              component={Link}
              to="/login"
              onClick={handleMobileMenuToggle}
              sx={{ color: 'text.primary', textDecoration: 'none' }}
            >
              <ListItemText primary="Connexion" />
            </ListItem>
            <ListItem
              component={Link}
              to="/register"
              onClick={handleMobileMenuToggle}
              sx={{ color: 'text.primary', textDecoration: 'none' }}
            >
              <ListItemText primary="Inscription" />
            </ListItem>
          </>
        )}
      </List>
    </Drawer>
  );

  return (
    <AppBar position="sticky" className="header">
      <Container maxWidth="xl">
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <img
              src="/assets/images/logo.png"
              alt="Mission Sport"
              style={{
                height: isMobile ? '40px' : '50px',
              }}
            />
          </Box>

          {/* Desktop Navigation */}
          {renderDesktopMenu()}

          {/* Mobile Menu Button */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              onClick={handleMobileMenuToggle}
              sx={{ color: 'text.primary' }}
              aria-label="menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>
      
      {/* Mobile Menu */}
      {renderMobileMenu()}
    </AppBar>
  );
};

export default Header;